import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getFirebaseClients } from '../firebase';
import { toIsoString } from '../schemas/common';
import { parseDocs } from './parseDocs';
import {
  galleryItemInputSchema,
  galleryItemSchema,
  type GalleryItem,
  type GalleryItemInput,
} from '../schemas/gallery';

const COLLECTION = 'gallery';

function db() {
  return getFirebaseClients().firestore;
}

function parse(snapshotData: unknown, id: string): GalleryItem {
  const data = snapshotData as Record<string, unknown>;
  return galleryItemSchema.parse({
    ...data,
    id,
    takenAt: data.takenAt ? toIsoString(data.takenAt) : undefined,
    createdAt: toIsoString(data.createdAt),
    updatedAt: toIsoString(data.updatedAt),
  });
}

/**
 * Public-visible gallery items.
 *
 * Firestore "trustless queries" — the rule rejects a query if ANY potentially
 * matching doc would fail the rule check. We therefore add an explicit
 * `where('visibility', '==', 'public')` so the rule can prove every returned
 * doc is readable. Same pattern for `listFeaturedGallery` and
 * `listGalleryByFormation` below.
 */
export async function listPublishedGallery(): Promise<GalleryItem[]> {
  const q = query(collection(db(), COLLECTION), where('visibility', '==', 'public'));
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION).sort(sortNewestFirst);
}

/** Items tagged to a given formation. Public-visible only. */
export async function listGalleryByFormation(formationId: string): Promise<GalleryItem[]> {
  const q = query(
    collection(db(), COLLECTION),
    where('formationId', '==', formationId),
    where('visibility', '==', 'public'),
  );
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION).sort(sortNewestFirst);
}

/**
 * Items flagged as featured on the home page. Public-visible only.
 *
 * Sorted by `displayOrder ASC` first so the admin can pin a specific order
 * in the home carousel (lower number = earlier). Ties — typically items
 * that still sit at the default 100 — fall back to newest-first so newly
 * uploaded vedette photos don't get buried until someone sets an order.
 */
export async function listFeaturedGallery(): Promise<GalleryItem[]> {
  const q = query(
    collection(db(), COLLECTION),
    where('featuredOnHome', '==', true),
    where('visibility', '==', 'public'),
  );
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION).sort(sortByDisplayOrderThenNewest);
}

/** Member-side: items where the member can see them on Mes inscriptions.
 *  Returns both `public` and `members` visibility; caller filters by formation
 *  / session as needed. Requires the caller to be signed in (rule check). */
export async function listGalleryForMember(): Promise<GalleryItem[]> {
  const q = query(
    collection(db(), COLLECTION),
    where('visibility', 'in', ['public', 'members']),
  );
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION).sort(sortNewestFirst);
}

/** Admin: every item, newest first. */
export async function listAllGallery(): Promise<GalleryItem[]> {
  const q = query(collection(db(), COLLECTION), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION).sort(sortNewestFirst);
}

function sortNewestFirst(a: GalleryItem, b: GalleryItem): number {
  const ad = a.takenAt ?? a.createdAt;
  const bd = b.takenAt ?? b.createdAt;
  if (ad !== bd) return bd.localeCompare(ad);
  return a.displayOrder - b.displayOrder;
}

/** Featured-home sort: explicit displayOrder wins, date breaks ties. */
function sortByDisplayOrderThenNewest(a: GalleryItem, b: GalleryItem): number {
  if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
  const ad = a.takenAt ?? a.createdAt;
  const bd = b.takenAt ?? b.createdAt;
  return bd.localeCompare(ad);
}

export async function createGalleryItem(input: GalleryItemInput): Promise<string> {
  const parsed = galleryItemInputSchema.parse(input);
  const now = new Date().toISOString();
  const ref = await addDoc(collection(db(), COLLECTION), {
    ...parsed,
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

export async function updateGalleryItem(
  id: string,
  patch: Partial<GalleryItemInput>,
): Promise<void> {
  await updateDoc(doc(db(), COLLECTION, id), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteGalleryItem(id: string): Promise<void> {
  await deleteDoc(doc(db(), COLLECTION, id));
}
